/**
 * Ruined portal (vanilla ruined_portal/portal_1..10): a broken obsidian frame
 * (some of it crying obsidian) standing on a stone-brick plinth in a patch of
 * netherrack and magma, with a gold block and a loot chest nearby. Underwater
 * ones skip the levelling.
 */

import { SEA_LEVEL } from '../../constants';
import { AIR, GOLD_BLOCK_ID, LAVA_ID } from '../../blocks';
import type { TerrainGenerator } from '../terrain';
import { block, type Builder, cell, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType } from './kit';

const BRICKS = cell.mix([
  [block('Stone Bricks'), 5], [block('Mossy Stone Bricks'), 3], [block('Cracked Stone Bricks'), 2],
]);

function buildPortal(b: Builder): void {
  const y = b.y, r = b.rnd;
  // scorched ground, thinning out from the centre
  for (let v = -1; v <= 7; v++) {
    for (let u = -1; u <= 7; u++) {
      const d = (u - 3) * (u - 3) + (v - 3) * (v - 3);
      if (d > 18 || r() < d / 26) continue;
      const roll = r();
      b.set(u, y, v, roll < 0.12 ? block('Magma Block') : roll < 0.16 ? LAVA_ID : block('Netherrack'));
    }
  }
  b.fill(1, y, 2, 5, y, 4, BRICKS);
  // the frame: 4 wide, 5 tall, missing more blocks the higher it goes
  for (let dy = 1; dy <= 5; dy++) {
    for (let u = 1; u <= 4; u++) {
      const frame = u === 1 || u === 4 || dy === 1 || dy === 5;
      if (!frame) { b.set(u, y + dy, 3, AIR); continue; }
      if (r() < (dy >= 4 ? 0.4 : 0.12)) continue;
      b.set(u, y + dy, 3, r() < 0.15 ? block('Crying Obsidian') : block('Obsidian'));
    }
  }
  b.set(5, y + 1, 4, GOLD_BLOCK_ID);
  b.chest(0, y + 1, 2, F.PU);
}

function ruinedPortal(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  return [centredPiece(x, z, int(rnd, 4), 7, 7, c.height, c.height >= SEA_LEVEL, seedOf(rnd), buildPortal)];
}

export const RUINED_PORTAL: StructureType = {
  name: 'Ruined Portal', spacing: 40, separation: 15, salt: 34222645, reach: 1, generate: ruinedPortal,
};
